import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type DirectoryUser, type ItemShareInfo } from "../api/client";
import {
  b64decode,
  b64encode,
  decryptItem,
  encryptItem,
  newWrappedItemKey,
  shareItemKeyTo,
  unwrapItemKey,
} from "../crypto/vault";

// Share one item with a colleague (§12): the item_key is HPKE-wrapped onto the recipient's public
// key in the browser; the link carries only a share id. Revoking a share ROTATES the item_key
// (§7.2/§12): a fresh key is minted here, the item is re-encrypted, and every REMAINING recipient
// is re-wrapped — all in the browser — so the removed recipient's key stops working.
export function ShareDialog({ itemId, orgKey, onClose }: { itemId: string; orgKey: Uint8Array; onClose: () => void }) {
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [recipient, setRecipient] = useState("");
  const [shares, setShares] = useState<ItemShareInfo[]>([]);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadShares = useCallback(() => {
    api.listItemShares(itemId).then((r) => setShares(r.shares)).catch(() => undefined);
  }, [itemId]);

  useEffect(() => {
    api.directory().then((r) => setUsers(r.users)).catch(() => undefined);
    loadShares();
  }, [loadShares]);

  async function share() {
    const user = users.find((u) => u.id === recipient);
    if (!user) {
      setError("Pick a recipient");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const summary = await api.getItem(itemId);
      const itemKey = unwrapItemKey(orgKey, b64decode(summary.wrapped_item_key));
      const wrapped = shareItemKeyTo(b64decode(user.public_key), itemKey);
      await api.createShare(itemId, user.id, b64encode(wrapped));
      setRecipient("");
      loadShares();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Share failed");
    } finally {
      setBusy(false);
    }
  }

  // Revoke + rotate: decrypt the item under the current key, mint a new item_key, re-encrypt, and
  // re-wrap it for the org and every remaining recipient. The server applies it all atomically,
  // deletes the revoked share, and purges old versions (§26).
  async function revoke(target: ItemShareInfo) {
    setBusy(true);
    setError(null);
    try {
      const summary = await api.getItem(itemId);
      const oldItemKey = unwrapItemKey(orgKey, b64decode(summary.wrapped_item_key));
      const content = decryptItem<object>(oldItemKey, b64decode(summary.enc_content));
      const { itemKey: newItemKey, wrapped: newOrgWrap } = newWrappedItemKey(orgKey);
      const encContent = encryptItem(newItemKey, content);
      const remaining = shares
        .filter((s) => s.id !== target.id)
        .map((s) => ({
          share_id: s.id,
          wrapped_item_key: b64encode(shareItemKeyTo(b64decode(s.public_key), newItemKey)),
        }));
      await api.revokeShare(itemId, {
        revoke_share_id: target.id,
        revision: summary.revision,
        wrapped_item_key: b64encode(newOrgWrap),
        enc_content: b64encode(encContent),
        remaining_shares: remaining,
      });
      setConfirmId(null);
      loadShares();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Revoke failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 grid place-items-center bg-black/50" onClick={onClose}>
      <div className="w-[440px] rounded-md border border-border bg-panel shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-4 py-3 font-semibold">Share item</div>
        <div className="flex flex-col gap-3 px-4 py-4 text-sm">
          <p className="text-xs text-muted">
            The secret is re-wrapped onto the recipient's key in your browser — the server never sees
            it. Revoking rotates the item's key so the removed recipient can no longer decrypt it.
          </p>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Recipient</span>
            <div className="flex gap-2">
              <select value={recipient} onChange={(e) => setRecipient(e.target.value)} className="flex-1 rounded border border-border bg-panel2 px-2 py-1.5">
                <option value="">— select —</option>
                {users
                  .filter((u) => !shares.some((s) => s.principal === u.id))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.login}
                    </option>
                  ))}
              </select>
              <button onClick={share} disabled={busy || !recipient} className="rounded bg-accent px-3 py-1.5 font-medium text-white hover:bg-accent2 disabled:opacity-60">
                {busy ? "…" : "Share"}
              </button>
            </div>
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wide text-muted">Shared with</span>
            {shares.length === 0 ? (
              <span className="text-xs text-muted">Not shared with anyone yet.</span>
            ) : (
              <ul className="flex flex-col gap-1">
                {shares.map((s) => (
                  <li key={s.id} className="flex items-center gap-2 rounded bg-panel2 px-2 py-1.5">
                    <span className="flex-1 font-mono text-xs">{s.login}</span>
                    {confirmId === s.id ? (
                      <>
                        <button onClick={() => revoke(s)} disabled={busy} className="rounded bg-danger px-2 py-0.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-60">
                          {busy ? "…" : "Confirm revoke"}
                        </button>
                        <button onClick={() => setConfirmId(null)} disabled={busy} className="rounded border border-border px-2 py-0.5 text-xs hover:border-accent">
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmId(s.id)} disabled={busy} className="rounded border border-border px-2 py-0.5 text-xs text-danger hover:border-danger disabled:opacity-60">
                        Revoke
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <div className="text-xs text-danger">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
          <button onClick={onClose} className="rounded border border-border px-3 py-1.5 hover:border-accent">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
